import * as angular from "angular";
import { Filter } from "../models/filter.model";
import { FilterService } from "../services/filter.service";

export class FilterDataProvider {
    public filter: Filter;
    public appliedFilter: Filter;
    public numActiveFilters = 0;
    public numAppliedFilters = 0;
    public numAppliedGroupFilters = 0;
    public non_empty_fitlers: any = {};
    public non_empty_applied_fitlers: any = {};
    public readOnly = false;
    private resource: any;
    private readonly $q: any;
    private page: string;
    private readonly __app_type: string;

    constructor(FilterService: FilterService, $q: any, __app_type: string) {
        this.resource = FilterService;
        this.$q = $q;
        this.__app_type = __app_type;
        this.filter = new Filter();
        this.appliedFilter = new Filter();
    }

    clearAll(): void {
        this.filter = new Filter();
        this.appliedFilter = new Filter();
        this.numActiveFilters = 0;
        this.numAppliedFilters = 0;
        this.non_empty_fitlers = {};
        this.non_empty_applied_fitlers = {};
    }

    setPage(page: string): void {
        if (page != this.page) {
            this.clearAll();
        }

        this.page = page;
    }

    saved(): Promise<any> {
        const deferred = this.$q.defer();
        this.resource.all().then(
            (response: object) => {
                const json = response["data"];
                deferred.resolve(json.data);
            },
            (error: object) => {
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    removeValues(filter: Filter): void {
        for (let key in filter) {
            if (
                key == "custom fields" ||
                key == "elections" ||
                key == "surveys"
            ) {
                filter[key] = [];
            } else if (filter.hasOwnProperty(key)) {
                filter[key] = {};
            }
        }
    }

    nonEmptyFilters(firstLoad: boolean): object {
        this.non_empty_fitlers = this.filter.nonEmptyValues();
        if (firstLoad) {
            for (let key in this.non_empty_fitlers) {
                if (key != "elections" && key != "custom_fields") {
                    this.filter[key].checked = true;
                } else if (key == "elections") {
                    for (var i = 0; i < this.filter.elections.length; i++) {
                        if (this.filter.elections[i].values.length)
                            this.filter.elections[i].checked = true;
                    }
                }
                firstLoad = false;
            }
        }
        return this.non_empty_fitlers;
    }

    nonEmptyAppliedFilters(firstLoad?: boolean): object {
        this.non_empty_applied_fitlers = this.appliedFilter.nonEmptyValues();
        if (firstLoad) {
            for (let key in this.non_empty_applied_fitlers) {
                if (key != "elections" && key != "custom_fields") {
                    this.appliedFilter[key].checked = true;
                } else if (key == "elections") {
                    for (
                        var i = 0;
                        i < this.appliedFilter.elections.length;
                        i++
                    ) {
                        if (this.appliedFilter.elections[i].values.length)
                            this.appliedFilter.elections[i].checked = true;
                    }
                }
                firstLoad = false;
            }
        }
        this.getNumAppliedFilters();
        return this.non_empty_applied_fitlers;
    }

    getNumActiveFilters(): number {
        let num = 0;
        if (this.non_empty_fitlers.search) {
            num = Object.keys(this.non_empty_fitlers).length - 1;
        } else {
            num = Object.keys(this.non_empty_fitlers).length;
        }
        this.numActiveFilters = num;
        return num;
    }

    getNumAppliedFilters(): number {
        let num = 0;
        if (this.non_empty_applied_fitlers.search) {
            num = Object.keys(this.non_empty_applied_fitlers).length - 1;
        } else {
            num = Object.keys(this.non_empty_applied_fitlers).length;
        }
        if (this.non_empty_applied_fitlers.custom_fields) {
            num -= 1;
            num += this.non_empty_applied_fitlers.custom_fields.length;
        }
        if (this.non_empty_applied_fitlers.surveys) {
            num -= 1;
            angular.forEach(
                this.non_empty_applied_fitlers.surveys,
                (survey) => {
                    num += survey.questions.length;
                }
            );
        }
        if (this.non_empty_applied_fitlers.efforts) {
            num -= 1;
            for (const key in this.non_empty_applied_fitlers.efforts) {
                num += Object.keys(
                    this.non_empty_applied_fitlers.efforts[key]
                ).length;
            }
        }
        this.numAppliedGroupFilters = num;
        if (this.non_empty_applied_fitlers.assigned_users) {
            num -= 1;
        }
        if (this.non_empty_applied_fitlers.assigned_teams) {
            num -= 1;
        }
        if (this.non_empty_applied_fitlers.list_status) {
            num -= 1;
        }
        // In the admin panel `status` is owned by the quick-filter chips, not
        // by the filter pane, so it must not show in the pane's badge.
        if (
            this.__app_type === "ADMIN" &&
            this.non_empty_applied_fitlers.status
        ) {
            num -= 1;
        }
        this.numAppliedFilters = num;
        return num;
    }

    isSearchActive(): boolean {
        return (
            this.non_empty_applied_fitlers &&
            this.non_empty_applied_fitlers.search &&
            this.non_empty_applied_fitlers.search != ""
        );
    }

    toggleFilterCheckBox(
        filter: Filter,
        filterName: string,
        value: any,
        subFilterName: string,
        effortId: string | number
    ): void {
        if (this.readOnly) {
            return;
        }
        let haystack = effortId
            ? filter.efforts["e" + effortId][filterName].values
            : filter[filterName].values;

        if (subFilterName) {
            if (!haystack.hasOwnProperty(subFilterName))
                haystack[subFilterName] = [];
            haystack = haystack[subFilterName];
        }
        const index = haystack.indexOf(value);
        if (index > -1) {
            haystack.splice(index, 1);
        } else {
            haystack.push(value);
        }
    }

    isActiveFilterCheckbox(
        filter: Filter,
        filterName: string,
        value: any,
        subFilterName: string,
        effortId: string | number
    ): boolean {
        if (!effortId || filter.efforts["e" + effortId]) {
            let filters = effortId
                ? filter.efforts["e" + effortId][filterName]
                : filter[filterName];

            if (filters) {
                let haystack = filters.values;
                if (subFilterName) {
                    if (!haystack.hasOwnProperty(subFilterName))
                        haystack[subFilterName] = [];
                    haystack = haystack[subFilterName];
                }
                if (!angular.isUndefined(haystack)) {
                    return haystack.indexOf(value) > -1;
                }
            }
        }
        return false;
    }

    deleteGroupFilter(gid: number) {
        const deferred = this.$q.defer();
        this.resource.deleteGroupFilter(gid).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(json);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    createFilterWithGid(gid: number | string, gname: string, filter: object) {
        const deferred = this.$q.defer();
        this.resource.create({ gid: gid, fname: gname, filters: filter }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    json.data.fid = json.id;
                    deferred.resolve(json);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );

        return deferred.promise;
    }

    create(name: string, filter: object, type: string = null) {
        const deferred = this.$q.defer();
        this.resource.create({ fname: name, filters: filter, type: type }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    json.data.fid = json.id;
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );

        return deferred.promise;
    }

    reformatFilter(search: any, skipParse?: boolean): any {
        let new_filters = {};

        if (
            angular.isDefined(search) &&
            angular.isDefined(search.filter) &&
            !angular.equals({}, search)
        ) {
            let filters = skipParse ? search.filter : JSON.parse(search.filter);

            for (let key in filters) {
                if (
                    key != "custom_fields" &&
                    key != "elections" &&
                    key != "surveys" &&
                    key != "efforts"
                ) {
                    new_filters[key] = { values: filters[key] };
                } else {
                    new_filters[key] = filters[key];
                }
                if (key == "efforts" || key == "custom_fields") {
                    filters[key].checked = true;
                    angular.forEach(
                        filters[key],
                        (element: any, key: string) => {
                            if (key === "checked") return;
                            element.checked = true;
                        }
                    );
                } else {
                    new_filters[key].checked = true;
                }
            }
        }
        return new_filters;
    }
}

angular
    .module("vcDataProvider")
    .service("FilterDataProvider", FilterDataProvider);
